import { useEffect, useRef } from "react";
import type { IDataFilters, IDataRow } from "@/api/molecularData";

const BSAD_ID_KEY = "BSA-DB ID";

/**
 * Whenever the active filter criteria change — not merely the page or page
 * size — prunes the selection down to only the ids present in the freshly
 * filtered result, so a molecule that no longer matches can't stay selected
 * forever. Waits for the query to settle (`isFetching` false) so it prunes
 * against the NEW filters' data, not a stale `keepPreviousData` snapshot.
 *
 * Only checks the *current page* of the new filter's results — there's no
 * lightweight "does id X match filter Y" endpoint, so this can't verify
 * matches sitting on a later page without fetching the whole dataset. In
 * practice this still resolves the reported case (a molecule that no longer
 * matches the filter at all won't appear on any page, so it's pruned).
 */
export function useSyncSelectionWithFilters(
  filters: IDataFilters,
  rows: IDataRow[] | undefined,
  isFetching: boolean,
  pruneToVisible: (visibleIds: Set<string>) => void,
) {
  const lastPrunedForRef = useRef<IDataFilters | null>(null);

  useEffect(() => {
    if (!rows || isFetching) return;
    if (lastPrunedForRef.current === filters) return;
    lastPrunedForRef.current = filters;

    const visibleIds = new Set(
      rows
        .map((row) => row[BSAD_ID_KEY])
        .filter((id): id is string => typeof id === "string"),
    );
    pruneToVisible(visibleIds);
  }, [filters, rows, isFetching, pruneToVisible]);
}
